import { cssValueOf, isCssProperty } from "./css";
import { isRecord } from "./record";
import { elementSchemaOf, INSERT_NAME, LAYOUT_NAME } from "./schema";
import type {
	Decoration,
	DecorationCss,
	DecorationElement,
	DecorationElements,
	DecorationFontFace,
	DecorationFontFamily,
	DecorationFontFile,
	DecorationFonts,
	DecorationGridLayout,
	DecorationIcon,
	DecorationInsert,
	DecorationText,
} from "./types";

const UNSAFE_KEYS = new Set(["__proto__", "constructor", "prototype"]);

const RESERVED_FAMILIES = new Set(["bounded", "bounded-light", "bounded-black"]);

const UNUSABLE_FAMILY = /["'\\,;]/;

const FONT_URL = /^(https?:\/\/|data:)/i;

const MAX_FAMILY_LENGTH = 64;

const MAX_FAMILY_FILES = 8;

const MIN_FONT_WEIGHT = 1;

const MAX_FONT_WEIGHT = 1000;

const MAX_INSERT_DEPTH = 8;

const sanitizedOf = new WeakMap<object, Decoration>();

let recentScreens = new Map<string, DecorationElements>();

const compact = <T extends object>(value: T): T | undefined => {
	const entries = Object.entries(value).filter(([, item]) => item !== undefined);
	return entries.length > 0 ? (Object.fromEntries(entries) as T) : undefined;
};

const mapEntries = <T>(value: unknown, map: (key: string, item: unknown) => T | undefined): Record<string, T> | undefined => {
	if (!isRecord(value)) return undefined;

	const entries = Object.entries(value).flatMap(([key, item]): [string, T][] => {
		const mapped = UNSAFE_KEYS.has(key) ? undefined : map(key, item);
		return mapped === undefined ? [] : [[key, mapped]];
	});
	return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

const orNull = <T>(value: unknown, sanitize: (value: unknown) => T | undefined): T | null | undefined => (value === null ? null : sanitize(value));

const booleanOf = (value: unknown) => (typeof value === "boolean" ? value : undefined);

const finiteNumberOf = (value: unknown) => (typeof value === "number" && Number.isFinite(value) ? value : undefined);

const textOf = (value: unknown) => (typeof value === "string" && value.trim() !== "" ? value : undefined);

const isPositiveNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value) && value > 0;

const iconSizeOf = (value: unknown) => (isPositiveNumber(value) ? value : textOf(value));

const sanitizeCss = (value: unknown): DecorationCss | undefined =>
	mapEntries(value, (property, item) => (isCssProperty(property) ? cssValueOf(item) : undefined));

const sanitizeIcon = (value: unknown): DecorationIcon | undefined =>
	isRecord(value) ? compact({ url: textOf(value.url), size: iconSizeOf(value.size) }) : undefined;

const sanitizeVariant = (screen: string, element: string, value: unknown) =>
	typeof value === "string" && elementSchemaOf(screen, element)?.variant?.values.includes(value) ? value : undefined;

const sanitizeVisible = (screen: string, element: string, value: unknown) => (elementSchemaOf(screen, element)?.locked ? undefined : booleanOf(value));

const sanitizeText = (value: unknown): DecorationText | undefined => mapEntries(value, (_language, item) => textOf(item));

const sanitizeInserts = (value: unknown, depth = 1): DecorationInsert[] | undefined => {
	if (!Array.isArray(value) || depth > MAX_INSERT_DEPTH) return undefined;

	const seen = new Set<string>();
	const inserts = value.flatMap((item): DecorationInsert[] => {
		if (!isRecord(item) || typeof item.el !== "string" || !INSERT_NAME.test(item.el) || seen.has(item.el)) return [];
		seen.add(item.el);
		const text = sanitizeText(item.text);
		return [{ el: item.el, ...compact({ text, children: text ? undefined : sanitizeInserts(item.children, depth + 1) }) }];
	});
	return inserts.length > 0 ? inserts : undefined;
};

const sanitizeAnchorInserts = (screen: string, element: string, value: unknown) =>
	elementSchemaOf(screen, element)?.inserts ? sanitizeInserts(value) : undefined;

const sanitizePattern = (value: unknown, sizes: readonly string[]) =>
	Array.isArray(value) ? value.map((item) => (typeof item === "string" && sizes.includes(item) ? item : null)) : [];

const sanitizeLayout = (value: unknown, sizes: readonly string[]): DecorationGridLayout | undefined =>
	isRecord(value)
		? { pattern: sanitizePattern(value.pattern, sizes), ...compact({ repeat: booleanOf(value.repeat), inserts: sanitizeInserts(value.inserts) }) }
		: undefined;

const sanitizeLayouts = (screen: string, element: string, value: unknown) => {
	const sizes = elementSchemaOf(screen, element)?.layouts?.sizes;
	return sizes ? mapEntries(value, (name, item) => (LAYOUT_NAME.test(name) ? sanitizeLayout(item, sizes) : undefined)) : undefined;
};

const sanitizeSections = (screen: string, element: string, value: unknown) =>
	elementSchemaOf(screen, element)?.layouts
		? mapEntries(value, (_section, item) => (typeof item === "string" && LAYOUT_NAME.test(item) ? item : undefined))
		: undefined;

const sanitizeLimit = (screen: string, element: string, value: unknown) => {
	const limit = elementSchemaOf(screen, element)?.limit;
	if (!limit || typeof value !== "number" || !Number.isInteger(value)) return undefined;
	return Math.min(Math.max(value, limit.min), limit.max);
};

const sanitizeElement = (screen: string, element: string, value: unknown): DecorationElement | undefined =>
	isRecord(value)
		? compact({
				visible: sanitizeVisible(screen, element, value.visible),
				variant: sanitizeVariant(screen, element, value.variant),
				icon: sanitizeIcon(value.icon),
				css: sanitizeCss(value.css),
				before: sanitizeCss(value.before),
				after: sanitizeCss(value.after),
				inserts: sanitizeAnchorInserts(screen, element, value.inserts),
				layouts: sanitizeLayouts(screen, element, value.layouts),
				sections: sanitizeSections(screen, element, value.sections),
				limit: sanitizeLimit(screen, element, value.limit),
			})
		: undefined;

const sanitizeScreens = (value: unknown): Record<string, DecorationElements> | undefined => {
	const reusable = recentScreens;
	recentScreens = new Map();

	return mapEntries(value, (screen, elements) => {
		const fresh = mapEntries(elements, (element, item) => sanitizeElement(screen, element, item));
		if (!fresh) return undefined;

		const fingerprint = `${screen}\n${JSON.stringify(fresh)}`;
		const unchanged = reusable.get(fingerprint) ?? fresh;
		recentScreens.set(fingerprint, unchanged);
		return unchanged;
	});
};

const sanitizeFontFile = (value: unknown): DecorationFontFile | undefined => {
	if (!isRecord(value)) return undefined;

	const url = textOf(value.url);
	return url ? { url, ...compact({ name: textOf(value.name), size: finiteNumberOf(value.size) }) } : undefined;
};

const fontWeightOf = (value: unknown) =>
	typeof value === "number" && Number.isFinite(value) && value >= MIN_FONT_WEIGHT && value <= MAX_FONT_WEIGHT ? value : undefined;

const isFontStyle = (value: unknown): value is NonNullable<DecorationFontFace["style"]> => value === "normal" || value === "italic";

const fontStyleOf = (value: unknown) => (isFontStyle(value) ? value : undefined);

const isAbsentOr = (value: unknown, sanitize: (value: unknown) => unknown) => value === undefined || value === null || sanitize(value) !== undefined;

const fontUrlOf = (value: unknown) => {
	const url = textOf(value)?.trim();
	return url && FONT_URL.test(url) ? url : undefined;
};

const familyNameOf = (value: unknown) => {
	const family = typeof value === "string" ? value.trim() : "";
	const usable = family.length > 0 && family.length <= MAX_FAMILY_LENGTH && !UNUSABLE_FAMILY.test(family);
	return usable && !RESERVED_FAMILIES.has(family.toLowerCase()) ? family : undefined;
};

const sanitizeFontFace = (value: unknown): DecorationFontFace | undefined => {
	if (!isRecord(value) || !isAbsentOr(value.weight, fontWeightOf) || !isAbsentOr(value.style, fontStyleOf)) return undefined;

	const url = fontUrlOf(value.url);
	const details = compact({
		name: textOf(value.name),
		size: finiteNumberOf(value.size),
		weight: orNull(value.weight, fontWeightOf),
		style: orNull(value.style, fontStyleOf),
	});
	return url ? { url, ...details } : undefined;
};

const sanitizeFontFamily = (value: unknown): DecorationFontFamily | undefined => {
	if (!isRecord(value) || !Array.isArray(value.files)) return undefined;

	const family = familyNameOf(value.family);
	const files = value.files.flatMap((file) => sanitizeFontFace(file) ?? []).slice(0, MAX_FAMILY_FILES);
	return family && files.length > 0 ? { family, files } : undefined;
};

export const sanitizeFontFamilies = (value: unknown): DecorationFontFamily[] | undefined => {
	if (!Array.isArray(value)) return undefined;

	const seen = new Set<string>();
	const families = value.flatMap((item) => {
		const family = sanitizeFontFamily(item);
		const name = family?.family.toLowerCase();
		if (!family || !name || seen.has(name)) return [];
		seen.add(name);
		return [family];
	});
	return families.length > 0 ? families : undefined;
};

const sanitizeFonts = (value: unknown): DecorationFonts | undefined =>
	isRecord(value)
		? compact({
				main: orNull(value.main, sanitizeFontFile),
				light: orNull(value.light, sanitizeFontFile),
				bold: orNull(value.bold, sanitizeFontFile),
				weight_light: orNull(value.weight_light, finiteNumberOf),
				weight_bold: orNull(value.weight_bold, finiteNumberOf),
				fit: orNull(value.fit, booleanOf),
				scale: orNull(value.scale, finiteNumberOf),
				families: orNull(value.families, sanitizeFontFamilies),
			})
		: undefined;

const buildDecoration = (value: Record<string, unknown>): Decoration =>
	compact({
		hash_sum: textOf(value.hash_sum),
		fonts: orNull(value.fonts, sanitizeFonts),
		screens: sanitizeScreens(value.screens),
	}) ?? {};

export const sanitizeDecoration = (value: unknown): Decoration | null => {
	if (!isRecord(value)) return null;

	const known = sanitizedOf.get(value);
	if (known) return known;

	const decoration = buildDecoration(value);
	sanitizedOf.set(value, decoration);
	sanitizedOf.set(decoration, decoration);
	return decoration;
};
